<template>
  <app-page labelledby="accounting-settings-title">
    <page-header
      title-id="accounting-settings-title"
      title="Настройки бухгалтерии"
      description="Справочники и реквизиты, используемые в бухгалтерских документах."
    />

    <app-state
      v-if="pageState !== 'ready'"
      :state="pageState"
      @action="showReadyState"
      @reset="showReadyState"
      @retry="showReadyState"
    />

    <page-section
      v-else
      title="Справочники и реквизиты"
      description="Открывайте нужный раздел, чтобы поддерживать данные организации в актуальном состоянии."
    >
      <q-list class="settings-list" separator role="list">
        <q-item v-for="item in settingsItems" :key="item.routeName" class="settings-item" role="listitem">
          <q-item-section>
            <q-item-label class="settings-item__title">{{ item.title }}</q-item-label>
            <q-item-label caption class="settings-item__description">{{ item.description }}</q-item-label>
          </q-item-section>
          <q-item-section side class="settings-item__state">
            <status-badge
              :tone="item.status === 'ready' ? 'success' : item.status === 'error' ? 'warning' : 'neutral'"
              :icon="item.status === 'ready' ? 'check_circle' : item.status === 'error' ? 'error_outline' : 'sync'"
              :label="settingsStatusLabel(item)"
            />
            <span v-if="item.count !== null" class="settings-item__count">{{ recordsLabel(item.count) }}</span>
          </q-item-section>
          <q-item-section side class="settings-item__actions">
            <page-actions :aria-label="`Действия: ${item.title}`">
              <q-btn
                color="primary"
                flat
                no-caps
                :label="item.actionLabel"
                @click="$router.push({ name: item.routeName })"
              />
            </page-actions>
          </q-item-section>
        </q-item>
      </q-list>
    </page-section>
  </app-page>
</template>

<script>
import {
  AppPage,
  AppState,
  PageActions,
  PageHeader,
  PageSection,
  StatusBadge
} from '../../components/ui'

const SETTINGS_ITEMS = Object.freeze([
  {
    key: 'accounts',
    title: 'Настройки счетов',
    description: 'Расчетные счета организации',
    actionLabel: 'Управлять',
    routeName: 'org_accounts_list'
  },
  {
    key: 'counterparties',
    title: 'Контрагенты',
    description: 'Справочник контрагентов организации',
    actionLabel: 'Открыть',
    routeName: 'contragents_glossary'
  },
  {
    key: 'nomenclature',
    title: 'Номенклатура',
    description: 'Справочник товаров и услуг для УПД',
    actionLabel: 'Открыть',
    routeName: 'accounting_nomenclature'
  }
])

const DEV_FIXTURE_STATES = new Set([
  'loading',
  'empty',
  'filtered-empty',
  'error',
  'access-denied',
  'not-found',
  'unavailable',
  'offline'
])

function developmentFixtureState() {
  if (process.env.NODE_ENV === 'production' || typeof window === 'undefined') return 'ready'
  const fixture = window.__SID_E2E_PAGE_PRIMITIVES__
  return fixture && DEV_FIXTURE_STATES.has(fixture.state) ? fixture.state : 'ready'
}

export default {
  name: 'AutomaticSettings',
  components: {
    AppPage,
    AppState,
    PageActions,
    PageHeader,
    PageSection,
    StatusBadge
  },
  data() {
    return {
      pageState: developmentFixtureState(),
      settingsItems: SETTINGS_ITEMS.map((item) => ({ ...item, status: 'loading', count: null })),
      requestIds: {},
      handlers: []
    }
  },
  mounted() {
    this.bind('onGetOrgAccountsList', (response) => this.applyCountResponse('accounts', response, Array.isArray(response.data) ? response.data.length : null))
    this.bind('onGetCounterparties', (response) => {
      const pagination = response.data && response.data.pagination
      const items = response.data && response.data.items
      this.applyCountResponse('counterparties', response, pagination ? pagination.total : (Array.isArray(items) ? items.length : null))
    })
    this.bind('onListNomenclature', (response) => {
      const rows = response.nomenclature || response.abstract_positions
      this.applyCountResponse('nomenclature', response, Array.isArray(rows) ? rows.length : null)
    })
    const ready = () => this.loadDirectoryCounts()
    this.handlers.push(['connectionReady', ready])
    if (this.connection.isAuthenticated) ready()
    else this.emitter.on('connectionReady', ready)
  },
  beforeUnmount() {
    this.handlers.forEach(([name, handler]) => this.emitter.off(name, handler))
  },
  methods: {
    showReadyState() {
      this.pageState = 'ready'
    },
    bind(name, handler) {
      this.handlers.push([name, handler])
      this.emitter.on(name, handler)
    },
    loadDirectoryCounts() {
      this.settingsItems.forEach((item) => {
        item.status = 'loading'
        item.count = null
      })
      this.requestIds.accounts = this.connection.makeRequestId('settings-accounts')
      this.requestIds.counterparties = this.connection.makeRequestId('settings-counterparties')
      this.requestIds.nomenclature = this.connection.makeRequestId('settings-nomenclature')
      this.connection.getOrgAccountsList(this.requestIds.accounts)
      this.connection.getCounterparties(this.requestIds.counterparties, { page: 1, page_size: 1, include_archived: true })
      this.connection.listNomenclature(this.requestIds.nomenclature, { include_archived: true })
    },
    applyCountResponse(key, response, count) {
      if (response.request_id !== this.requestIds[key]) return
      const item = this.settingsItems.find((candidate) => candidate.key === key)
      if (!item) return
      if (response.type === 'error' || response.error) {
        item.status = 'error'
        item.count = null
        return
      }
      item.status = 'ready'
      item.count = Number.isFinite(Number(count)) ? Number(count) : null
    },
    settingsStatusLabel(item) {
      if (item.status === 'loading') return 'Проверяем'
      if (item.status === 'error') return 'Недоступно'
      return item.count === 0 ? 'Требует заполнения' : 'Готово'
    },
    recordsLabel(count) {
      const absolute = Math.abs(Number(count))
      const mod100 = absolute % 100
      const mod10 = absolute % 10
      const noun = mod100 >= 11 && mod100 <= 14
        ? 'записей'
        : mod10 === 1
          ? 'запись'
          : mod10 >= 2 && mod10 <= 4
            ? 'записи'
            : 'записей'
      return `${count} ${noun}`
    }
  }
}
</script>

<style scoped>
.settings-list {
  max-width: 52rem;
  overflow: hidden;
  border: 1px solid var(--sid-color-line);
  border-radius: var(--sid-radius-surface);
  background: var(--sid-color-surface-0);
}

.settings-item {
  min-height: var(--sid-row-comfortable);
  padding: var(--sid-space-3) var(--sid-space-4);
}

.settings-item__title {
  color: var(--sid-color-text-primary);
  font-weight: var(--sid-font-weight-medium);
}

.settings-item__description {
  margin-top: var(--sid-space-1);
  color: var(--sid-color-text-secondary);
}

.settings-item__state {
  align-items: flex-end;
  gap: var(--sid-space-1);
}

.settings-item__count {
  color: var(--sid-color-text-secondary);
  font-size: var(--sid-font-size-label);
  font-variant-numeric: tabular-nums;
}

@media (max-width: 599px) {
  .settings-item {
    align-items: stretch;
    flex-direction: column;
    gap: var(--sid-space-3);
  }

  .settings-item__actions {
    width: 100%;
    padding-left: 0;
  }

  .settings-item__state {
    align-items: flex-start;
    width: 100%;
    padding-left: 0;
  }
}
</style>
